import axios from 'axios';
import {API_BASE_URL} from './authApi';

interface FollowMutationResponse {
  success: boolean;
  userId: string;
  isFollowing: boolean;
  didChange?: boolean;
}

type FollowApiErrorResponse = {
  message?: string;
  error?: string | {message?: string};
};

const followClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data as FollowApiErrorResponse | undefined;
    const nestedError =
      typeof errorData?.error === 'string'
        ? errorData.error
        : errorData?.error?.message;

    return errorData?.message || nestedError || error.message || fallbackMessage;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallbackMessage;
};

const authorizedHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
});

export const followApi = {
  async followUser(token: string, userId: string, signal?: AbortSignal): Promise<FollowMutationResponse> {
    try {
      const response = await followClient.post<FollowMutationResponse>(
        '/follows',
        {userId},
        {headers: authorizedHeaders(token), signal}
      );

      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to follow user'));
    }
  },

  async unfollowUser(token: string, userId: string, signal?: AbortSignal): Promise<FollowMutationResponse> {
    try {
      const response = await followClient.delete<FollowMutationResponse>(
        `/follows/${userId}`,
        {headers: authorizedHeaders(token), signal}
      );

      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to unfollow user'));
    }
  },
};
